import React from 'react'
import { cn } from '../../lib/utils'
import { NavProvider, useNav } from './NavProvider'
import { UserIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import NavGroup from './NavGroup'
import { ActionPlanProvider } from '../ActionPlan/ActionPlanProvider'
import useTranslation, {
  TranslationProvider,
} from '~/components/shared/TranslationProvider'
import translations from './locales.json'
import { Provider } from 'react-redux'
import { store } from '../../lib/store'
import { setGoal, setTimeline } from '../ActionPlan/ActionPlanSlice'
import GoalHeader from './GoalHeader'

const LeftNav = ({ locale, ...props }) => {
  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope="javascript.components.LeftNav"
    >
      <NavProvider locale={locale} {...props}>
        <Provider store={store}>
          <ActionPlanProvider {...props}>
            <LeftNavInner />
          </ActionPlanProvider>
        </Provider>
      </NavProvider>
    </TranslationProvider>
  )
}
export default LeftNav

const LeftNavInner = () => {
  const { userName, userGoal, userTimeline, navItems, isExpanded, showGoal } =
    useNav()

  // Use useEffect to dispatch actions after render
  React.useEffect(() => {
    store.dispatch(setGoal(userGoal))
    store.dispatch(setTimeline(userTimeline))
  }, [userGoal, userTimeline])

  return (
    <VerticalNav>
      <div className="grid h-full min-h-0 grid-rows-[auto_auto_1fr] [&_.nav-divider:first-child]:mt-0">
        <UsernameHeader>{userName}</UsernameHeader>
        {isExpanded && showGoal && (
          <div className="mb-5 px-4 pt-6">
            <GoalHeader />
          </div>
        )}
        <div
          id="nav-groups"
          className="flex h-full min-h-0 flex-col gap-y-2 overflow-y-auto"
        >
          {navItems.map((section, sectionIndex) => (
            <NavGroup key={sectionIndex} section={section}></NavGroup>
          ))}
        </div>
      </div>
    </VerticalNav>
  )
}

const UsernameHeader = ({ children }) => {
  const { isExpanded, showUser } = useNav()
  return showUser ? (
    <a href="/users/edit" className="cursor-pointer hover:text-cod-gray-800">
      <div className="mb-6 flex h-11 w-full flex-row items-center justify-center">
        <div className="m-3 h-6 w-6 text-cod-gray-800" aria-hidden="true">
          <UserIcon />
        </div>
        {isExpanded && (
          <div className="ml-3 flex-grow text-sm font-medium text-cod-gray-800">
            {children}
          </div>
        )}
      </div>
    </a>
  ) : (
    <div />
  )
}

const ToggleExpanded = () => {
  const { toggleExpanded, isExpanded, isMobile } = useNav()
  const { t } = useTranslation()
  const label = isExpanded
    ? t('collapse_menu', { default: 'Collapse menu' })
    : t('expand_menu', { default: 'Expand menu' })
  const defaultToggleClasses = cn(
    'absolute -right-4 top-8 rounded border border-solid border-[#F6F6F6] bg-white p-1.5 cursor-pointer z-10',
    isMobile ? 'hidden' : 'block',
  )

  const toggleClasses = isExpanded
    ? defaultToggleClasses
    : cn(defaultToggleClasses, 'top-6')

  return (
    <button
      type="button"
      onClick={toggleExpanded}
      className={toggleClasses}
      aria-expanded={isExpanded}
      aria-label={label}
    >
      <ChevronRightIcon
        aria-hidden="true"
        className={cn(
          'h-r w-4 transition-all duration-500',
          isExpanded && 'rotate-180',
        )}
      />
    </button>
  )
}

const VerticalNav = ({ children }) => {
  const { isExpanded } = useNav()
  const { t } = useTranslation()
  return (
    <nav
      aria-label={t('main_navigation', { default: 'Main navigation' })}
      className={cn(
        'relative h-[calc(100vh-80px)] border-b border-r border-[#0000001a] font-roboto subpixel-antialiased transition-all duration-300',
        isExpanded ? 'w-full sm:w-64' : 'w-24',
      )}
    >
      <ToggleExpanded />
      {children}
    </nav>
  )
}
